import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Edge, Node } from 'reactflow';
import { useDebounceEffect } from '../useDebounceEffect';
import { nodeDataAutoSaveDynamic } from './nodedata_autosave';

type SetSaving = (saving: {
  lastSaved?: string;
  status: string;
  finished?: boolean;
  flowId?: string;
}) => void;

type SetSchema = (
  schema: Record<string, { type: string; required: boolean }>,
) => void;

export interface UseNodeFieldAutoSaveOptions<T> {
  externalValue: T;
  fieldName: string;
  nodeId: string;
  flowId: string | undefined;
  edges: Edge[];
  setNodeState: Dispatch<SetStateAction<Node[]>>;
  setSaving: SetSaving;
  setSchema: SetSchema;
  changeType?: string;
  debounceMs?: number;
}

const MIN_IN_FLIGHT_GATE_MS = 1500;

export function useNodeFieldAutoSave<T>(
  opts: UseNodeFieldAutoSaveOptions<T>,
): [T, (value: T) => void] {
  const {
    externalValue,
    fieldName,
    nodeId,
    flowId,
    edges,
    setNodeState,
    setSaving,
    setSchema,
    changeType = 'default',
    debounceMs = 500,
  } = opts;

  const [local, setLocal] = useState<T>(externalValue);
  const dirtyRef = useRef(false);
  const inFlightRef = useRef(0);

  const setLocalValue = (value: T) => {
    dirtyRef.current = true;
    setLocal(value);
  };

  useDebounceEffect(
    () => {
      if (!dirtyRef.current) return;
      dirtyRef.current = false;
      inFlightRef.current += 1;
      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id: nodeId,
        setSaving,
        flowId,
        changeType,
        objectData: { [fieldName]: local },
        setSchema,
      });
      const gateMs = Math.max(debounceMs * 3, MIN_IN_FLIGHT_GATE_MS);
      setTimeout(() => {
        inFlightRef.current = Math.max(0, inFlightRef.current - 1);
      }, gateMs);
    },
    [local],
    debounceMs,
  );

  useEffect(() => {
    if (dirtyRef.current) return;
    if (inFlightRef.current > 0) return;
    if (externalValue === local) return;
    setLocal(externalValue);
  }, [externalValue]);

  return [local, setLocalValue];
}
